import type {
	ActivityTriggerAction,
	ActivityTriggerFeature,
	ActivityTriggerFieldId,
	ActivityTriggerMappingProperty,
	ActivityTriggerOverlap,
	ActivityTriggerParseResult,
	ActivityTriggerRoleCode,
	ParsedActivityTriggerFeature
} from "./types";

const roleDefinitions: Array<{
	code: ActivityTriggerRoleCode;
	mappingProperty: ActivityTriggerMappingProperty;
	label: string;
	icon: string;
}> = [
	{
		code: "start",
		mappingProperty: "IsStart",
		label: "Start",
		icon: "zmdi-play"
	},
	{
		code: "end",
		mappingProperty: "IsEnd",
		label: "End",
		icon: "zmdi-stop"
	},
	{
		code: "point",
		mappingProperty: "IsPoint",
		label: "Point",
		icon: "zmdi-circle"
	}
];

function parseFeature(feature: ActivityTriggerFeature, index: number): ParsedActivityTriggerFeature {
	const featureId = feature.Id ?? index;
	const groups = roleDefinitions.reduce<ActivityTriggerAction[]>((parsedGroups, role) => {
		const fieldIds = (feature.Fields ?? []).reduce<ActivityTriggerFieldId[]>((ids, mapping) => {
			const fieldId = mapping.Field?.Id;
			if(mapping[role.mappingProperty] === true
				&& fieldId !== undefined
				&& !ids.includes(fieldId)) {
				ids.push(fieldId);
			}

			return ids;
		}, []);

		if(fieldIds.length > 0) {
			parsedGroups.push({
				...role,
				featureId,
				key: `${featureId}:${role.code}`,
				fieldIds
			});
		}

		return parsedGroups;
	}, []);

	return {
		featureId,
		groups
	};
}

export function parseActivityTriggerFeatures(features: ActivityTriggerFeature[] = []): ActivityTriggerParseResult {
	const parsedFeatures = features
		.filter(feature => feature.Type === "ActivityTrigger")
		.map(parseFeature)
		.filter(feature => feature.groups.length > 0);

	const owningFeatureByFieldId = new Map<ActivityTriggerFieldId, ParsedActivityTriggerFeature>();
	const overlapsByFieldId = new Map<ActivityTriggerFieldId, ActivityTriggerOverlap>();

	parsedFeatures.forEach(feature => {
		const featureFieldIds = new Set(feature.groups.flatMap(group => group.fieldIds));
		featureFieldIds.forEach(fieldId => {
			const owningFeature = owningFeatureByFieldId.get(fieldId);

			if(owningFeature !== undefined && owningFeature !== feature) {
				const overlap = overlapsByFieldId.get(fieldId) ?? {
					fieldId,
					featureIds: [owningFeature.featureId]
				};

				if(!overlap.featureIds.includes(feature.featureId)) {
					overlap.featureIds.push(feature.featureId);
				}

				overlapsByFieldId.set(fieldId, overlap);
			}
			else {
				owningFeatureByFieldId.set(fieldId, feature);
			}
		});
	});

	const overlaps = Array.from(overlapsByFieldId.values());
	return {
		valid: overlaps.length === 0,
		overlaps,
		features: overlaps.length === 0 ? parsedFeatures : []
	};
}
